import {
  Actions,
  Button,
  Divider,
  Stack,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
  Text,
} from 'braid-design-system';
import React from 'react';

import type { EventInput, EventRecord } from '#shared/types';
import {
  deleteEvent,
  fetchAdminEvents,
  publishEvent,
  unpublishEvent,
  updateEvent,
} from '#src/api/events';
import { getAccessToken } from '#src/utils/cognitoAuth';

import { CreateEventDialog } from './CreateEventDialog';
import { EventDetail } from './EventDetail';

type EventsAdminProps = {
  onError: (message: string | null) => void;
};

const EventList = ({
  events,
  emptyMessage,
  renderItem,
}: {
  events: EventRecord[];
  emptyMessage: string;
  renderItem: (event: EventRecord) => React.ReactNode;
}) => {
  if (events.length === 0) {
    return <Text>{emptyMessage}</Text>;
  }

  return (
    <Stack space="medium">
      {events.map((event, index) => (
        <React.Fragment key={event.id}>
          {index > 0 ? <Divider /> : null}
          {renderItem(event)}
        </React.Fragment>
      ))}
    </Stack>
  );
};

export const EventsAdmin = ({ onError }: EventsAdminProps) => {
  const [drafts, setDrafts] = React.useState<EventRecord[]>([]);
  const [published, setPublished] = React.useState<EventRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

  const loadEvents = React.useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      return;
    }

    const [draftList, publishedList] = await Promise.all([
      fetchAdminEvents(token, 'draft'),
      fetchAdminEvents(token, 'published'),
    ]);
    setDrafts(draftList);
    setPublished(publishedList);
  }, []);

  React.useEffect(() => {
    loadEvents()
      .catch(() => onError('Failed to load events'))
      .finally(() => setLoading(false));
  }, [loadEvents, onError]);

  const formatApiError = (err: unknown): string => {
    if (err instanceof Error) {
      return err.message;
    }
    if (err && typeof err === 'object') {
      if ('errors' in err) {
        return Object.values(
          (err as { errors: Record<string, string> }).errors,
        ).join(' ');
      }
      if ('error' in err) {
        return String((err as { error: string }).error);
      }
    }
    return 'Action failed';
  };

  const requireToken = (): string => {
    const token = getAccessToken();
    if (!token) {
      throw new Error('Session expired. Please log in again.');
    }
    return token;
  };

  const withToken = async (action: (token: string) => Promise<void>) => {
    try {
      onError(null);
      await action(requireToken());
      await loadEvents();
    } catch (err) {
      onError(formatApiError(err));
      throw err instanceof Error ? err : new Error(formatApiError(err));
    }
  };

  const saveEvent = async (eventId: string, input: EventInput) => {
    await updateEvent(requireToken(), eventId, input);
    onError(null);
    await loadEvents();
  };

  const publishDraft = async (eventId: string, input: EventInput) => {
    await publishEvent(requireToken(), eventId, input);
    onError(null);
    await loadEvents();
  };

  if (loading) {
    return <Text>Loading events…</Text>;
  }

  return (
    <Stack space="medium">
      <TabsProvider>
        <Tabs label="Event queues">
          <Tab item="draft">Draft ({drafts.length})</Tab>
          <Tab item="published">Published ({published.length})</Tab>
        </Tabs>
        <TabPanels>
          <TabPanel item="draft">
            <EventList
              events={drafts}
              emptyMessage="No draft events."
              renderItem={(event) => (
                <EventDetail
                  event={event}
                  onSave={(input) => saveEvent(event.id, input)}
                  onPublish={(input) => publishDraft(event.id, input)}
                  onDelete={() =>
                    withToken((token) => deleteEvent(token, event.id))
                  }
                />
              )}
            />
          </TabPanel>

          <TabPanel item="published">
            <EventList
              events={published}
              emptyMessage="No published events."
              renderItem={(event) => (
                <EventDetail
                  event={event}
                  onSave={(input) => saveEvent(event.id, input)}
                  onUnpublish={() =>
                    withToken((token) => unpublishEvent(token, event.id))
                  }
                />
              )}
            />
          </TabPanel>
        </TabPanels>
      </TabsProvider>

      <Actions>
        <Button tone="formAccent" onClick={() => setCreateDialogOpen(true)}>
          Create event
        </Button>
      </Actions>

      <CreateEventDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={loadEvents}
        onError={onError}
      />
    </Stack>
  );
};
